import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, firstValueFrom } from 'rxjs';
import { catchError, concatMap, map, pairwise, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { LocationsControllerService } from '@nx-customer-apps/api-planning-projects';
import { LocaleUtils } from '@nx-customer-apps/shared/utils';
import { SnackbarService, WindowService } from '@nx-customer-apps/shared/services';
import { CountryCode, CountryLanguageCodes, RoutePaths } from '@nx-customer-apps/shared/enums';
import { UserResult } from '@nx-customer-apps/shared/interfaces';
import { BuildingService } from '@nx-customer-apps/components';
import { LocaleService } from '../../services/locale.service';
import { AuthService, EnvironmentService, OnboardingService } from '../../services';
import { SettingsActions } from './settings.actions';
import { AuthStore } from '../auth/auth.store';
import { AuthActions } from '../auth/auth.actions';
import { SettingsStore } from './settings.store';
import { RollOutPolicy } from '../../policies';

@Injectable()
export class SettingsEffects {
    private actions$ = inject(Actions);
    private translateService = inject(TranslateService);
    private localeService = inject(LocaleService);
    private windowService = inject(WindowService);
    private locationsControllerService = inject(LocationsControllerService);
    private snackbarService = inject(SnackbarService);
    private environmentService = inject(EnvironmentService);
    private authStore = inject(AuthStore);
    private settingsStore = inject(SettingsStore);
    private authService = inject(AuthService);
    private onboarding = inject(OnboardingService);
    private rollOutPolicy = inject(RollOutPolicy);
    private buildingService = inject(BuildingService);

    initDefaultLocale$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SettingsActions.initDefaultLocale),
                tap(() => {
                    this.translateService.setDefaultLang(this.localeService.initLocale);
                })
            ),
        {
            dispatch: false
        }
    );

    initLocale$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SettingsActions.initLocale),
            withLatestFrom(this.authStore.me$, this.rollOutPolicy.getCountryLanguagePairs$(), (action, me, rolledOutLocales) => ({
                locale: action.locale,
                me,
                rolledOutLocales
            })),
            switchMap(payload => {
                const setLocale$ = (locale: string) =>
                    SettingsActions.setLocale({
                        locale
                    });

                if (this.localeService.locale) {
                    this.setLocale(payload.locale, payload.rolledOutLocales, payload.me);
                    return [setLocale$(this.localeService.locale)];
                }

                const parsedLocale = LocaleUtils.parseLocale(payload.locale);
                const isLocaleRolledOut = payload.rolledOutLocales.some(pair => {
                    return pair.locale === parsedLocale;
                });

                if (isLocaleRolledOut) {
                    this.setLocale(parsedLocale, payload.rolledOutLocales, payload.me);
                    return [setLocale$(parsedLocale)];
                }
                const locale = LocaleUtils.defaultBrowserLocale;
                this.translateService.setDefaultLang(locale);
                this.localeService.locale = locale;
                return [setLocale$(locale)];
            })
        )
    );

    changeLocale$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SettingsActions.changeLocale, SettingsActions.setLocale),
                pairwise(),
                // TODO: entire concatMap should be removed because this is a temporary solution
                // before Question Flow handles level.name translation dynamically
                concatMap(async ([previousAction, currentAction]) => {
                    if (currentAction.locale !== previousAction.locale) {
                        await firstValueFrom(this.translateService.use(currentAction.locale));
                        await this.buildingService.syncLocalizedNamesBeforeFullReload();
                    }

                    return [previousAction, currentAction];
                }),
                tap(([previousAction, currentAction]) => {
                    this.localeService.locale = currentAction.locale as string;
                    this.translateService.setDefaultLang(currentAction.locale);
                    if (!currentAction.reload) {
                        return;
                    }

                    const previousCountryCode = LocaleUtils.getCountryCode(previousAction.locale);
                    const currentCountryCode = LocaleUtils.getCountryCode(currentAction.locale);

                    if (previousCountryCode === currentCountryCode) {
                        this.windowService.reload();
                        return;
                    }

                    if (currentAction.reloadUrl) {
                        this.windowService.changeHref(currentAction.reloadUrl);
                        return;
                    }

                    const projectListUrl = `${this.environmentService.baseUrl}/${RoutePaths.Projects}`;
                    this.windowService.changeHref(projectListUrl);
                })
            ),
        {
            dispatch: false
        }
    );

    setLocale$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SettingsActions.setLocale),
                tap(() => {
                    this.onboarding.openDialog();
                })
            ),
        {
            dispatch: false
        }
    );

    getSalesOrganizations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SettingsActions.getSalesOrganizations),
            switchMap(() => {
                return this.locationsControllerService.locationsControllerGetOrganizations();
            }),
            map(organizations => SettingsActions.getSalesOrganizationsSuccess(organizations)),
            catchError(() => {
                const message = this.translateService.instant('COMMON.ERRORS.SOMETHING_WRONG');
                const action = this.translateService.instant('COMMON.BUTTONS.GOT_IT');
                this.snackbarService.showInfo(message, {
                    duration: 5000,
                    action
                });
                return EMPTY;
            })
        )
    );

    authUserHotJarLocale$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SettingsActions.setLocale),
                tap(action => {
                    // Set HotJar locale for authenticated user
                    const userId = this.authService.userId;
                    this.setHotJarLocale(action.locale, userId);
                })
            ),
        {
            dispatch: false
        }
    );

    nonAuthUserHotJarLocale$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.getCsrfTokenError),
                withLatestFrom(this.settingsStore.locale$, (action, locale) => ({ locale })),
                tap(({ locale }) => {
                    // Set HotJar locale for non authenticated user
                    this.setHotJarLocale(locale!, null);
                })
            ),
        {
            dispatch: false
        }
    );

    private setLocale(locale: string, rolledOutLocales: { key: string; locale: CountryLanguageCodes }[], me: UserResult): void {
        const storedLocale = this.localeService.locale;
        if (storedLocale) {
            if (storedLocale === locale) {
                this.translateService.setDefaultLang(storedLocale);
                return;
            }

            if (!this.authService.isInstaller(me)) {
                return;
            }

            const [storedLanguage, storedCountry] = storedLocale.split('-');
            const [_language, country] = locale.split('-');
            const countryLanguageDictionary = this.getCountryLanguageDictionary(rolledOutLocales);
            if (storedCountry === country && !countryLanguageDictionary[country as CountryCode]?.includes(storedLanguage)) {
                this.translateService.setDefaultLang(locale);
                this.localeService.locale = locale;
                return;
            }

            if (storedCountry !== country) {
                this.translateService.setDefaultLang(locale);
                this.localeService.locale = locale;
            }
        } else {
            this.translateService.setDefaultLang(locale);
            this.localeService.locale = locale;
        }
    }

    private setHotJarLocale(locale: string, userId: string | null): void {
        if (!this.environmentService.hotJarEnabled) {
            return;
        }

        const applicationLanguageCode = LocaleUtils.getLanguageCode(locale);
        const applicationCountryCode = LocaleUtils.getCountryCode(locale);
        // Wait for HotJar to be loaded
        const timeout = setInterval(() => {
            // @ts-ignore
            if (this.windowService.nativeWindow.hj) {
                // @ts-ignore
                this.windowService.nativeWindow.hj('identify', userId, {
                    applicationCountryCode,
                    applicationLanguageCode
                });
                clearInterval(timeout);
            }
        }, 1000);
    }

    private getCountryLanguageDictionary = (target: { key: string; locale: CountryLanguageCodes }[]): { [property: string]: string[] } => {
        return target.reduce(
            (acc, { locale }) => {
                const countryCode = LocaleUtils.getCountryCode(locale);
                const languageCode = LocaleUtils.getLanguageCode(locale);
                if (!acc[countryCode]) {
                    acc[countryCode] = [];
                }
                acc[countryCode].push(languageCode);

                return acc;
            },
            {} as { [property: string]: string[] }
        );
    };
}
